import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import "../assets/css/courses.css";

function getCurrentAcademicSession() {
  const year = new Date().getFullYear();
  return `${year}/${year + 1}`;
}

export default function CoursesRegistrationPage() {
  const [programmeName, setProgrammeName] = useState("");
  const [levels, setLevels] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [available, setAvailable] = useState([]);
  const [registered, setRegistered] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [level, setLevel] = useState("");
  const [semester, setSemester] = useState("");
  const [academicSession, setAcademicSession] = useState(
    getCurrentAcademicSession(),
  );
  const [initialLoading, setInitialLoading] = useState(true);
  const [message, setMessage] = useState("");

  const [studentLevel, setStudentLevel] = useState("");
  const [studentSession, setStudentSession] = useState("");
  const [studentDepartment, setStudentDepartment] = useState("");

  const [paymentStatus, setPaymentStatus] = useState(null);
  const canRegister =
    String(paymentStatus || "").toLowerCase() === "paid" ||
    String(paymentStatus || "").toLowerCase() === "success" ||
    String(paymentStatus || "").toLowerCase() === "completed" ||
    String(paymentStatus || "").toLowerCase() === "00";

  const [processingRegister, setProcessingRegister] = useState(false);
  const [processingRemoveIds, setProcessingRemoveIds] = useState(new Set());
  const [showCarryoverOnly, setShowCarryoverOnly] = useState(false);

  const token = localStorage.getItem("token");
  const matricNo = localStorage.getItem("matricNo");
  const API =
    process.env.NODE_ENV === "production"
      ? process.env.REACT_APP_API_BASE_URL
      : "http://localhost:3003";

  const authHeaders = () => ({ Authorization: `Bearer ${token}` });

  const numericLevel = (lvlStr) => {
    if (lvlStr == null) return NaN;
    const digits = ("" + lvlStr).match(/\d+/);
    if (!digits) return NaN;
    return parseInt(digits[0], 10);
  };

  const formatLevelString = (raw, example = "") => {
    if (Number.isNaN(raw)) return "";
    if (example && example.length >= 3) {
      return String(raw);
    }
    return String(raw);
  };

  const previousLevelOf = (lvlStr) => {
    const n = numericLevel(lvlStr);
    if (Number.isNaN(n)) return null;
    let prev;
    if (n >= 100) prev = n - 100;
    else prev = n - 1;
    return prev > 0 ? formatLevelString(prev, lvlStr) : null;
  };

  const isCarryoverCourse = (course) => {
    if (!course) return false;
    return Boolean(
      course.Carryover === 1 ||
      course.Carryover === true ||
      course.CarryOver === 1 ||
      course.CarryOver === true ||
      String(course.IsCarryover).toLowerCase() === "true" ||
      String(course.isCarryover).toLowerCase() === "true" ||
      course.Carry === 1 ||
      course.Carry === true,
    );
  };

  useEffect(() => {
    async function init() {
      try {
        setInitialLoading(true);
        setMessage("");
        const headers = authHeaders();
        const [{ data: user }, { data: bio }, { data: pay }] =
          await Promise.all([
            axios.get(`${API}/api/users/${matricNo}`, { headers }),
            axios.get(`${API}/api/biodata/${matricNo}`, { headers }),
            axios.get(`${API}/api/payments/latest`, {
              params: { matricNo },
              headers,
            }),
          ]);

        setProgrammeName(user.programmeName || "");

        if (bio?.Level || bio?.level) {
          const lvl = String(bio.Level ?? bio.level);
          setLevel(lvl);
          setStudentLevel(lvl);
        }

        if (bio?.session) {
          const sem = String(bio.session);
          setSemester(sem);
          setStudentSession(sem);
        }

        if (bio?.department) setStudentDepartment(String(bio.department));
        else if (bio?.Department) setStudentDepartment(String(bio.Department));

        if (pay && typeof pay === "object") {
          setPaymentStatus(pay.Status ?? null);
        } else {
          setPaymentStatus(null);
        }
      } catch (err) {
        console.error("Init load error:", err);
        setPaymentStatus(null);
        setMessage("Failed to load profile/payment info — try reloading.");
      } finally {
        setInitialLoading(false);
      }
    }
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!programmeName) return;
    axios
      .get(`${API}/api/courses/levels`, {
        params: { programmeName },
        headers: authHeaders(),
      })
      .then((res) => {
        const allLevels = Array.isArray(res.data) ? res.data : [];
        setLevels(allLevels);
      })
      .catch((err) => {
        console.error("Error fetching levels:", err);
        setLevels([]);
      });
  }, [API, programmeName]);

  useEffect(() => {
    if (!programmeName || !level) return;
    axios
      .get(`${API}/api/courses/semesters`, {
        params: { programmeName, level },
        headers: authHeaders(),
      })
      .then((res) => {
        const allSemesters = Array.isArray(res.data) ? res.data : [];
        setSemesters(allSemesters);
      })
      .catch((err) => {
        console.error("Error fetching semesters:", err);
        setSemesters([]);
      });
  }, [API, programmeName, level]);

  const fetchCourseLists = async (lvl, sem, dept, carryoverOnly = false) => {
    if (!programmeName || !sem) return;

    try {
      const headers = authHeaders();
      const requestedLevel = lvl || studentLevel || level;
      const requestedSession = sem || studentSession || semester;
      const prevLevel = previousLevelOf(requestedLevel);

      const levelToFetch = carryoverOnly ? prevLevel : requestedLevel;

      if (!levelToFetch) {
        setAvailable([]);
        setRegistered([]);
        return;
      }

      const courseResp = await axios.get(`${API}/api/courses`, {
        params: {
          programmeName,
          level: levelToFetch,
          semester: requestedSession,
          department: dept,
        },
        headers,
      });

      let courseList = Array.isArray(courseResp.data) ? courseResp.data : [];

      courseList = courseList.filter((c) => {
        const cLevel = String(c.Level ?? c.CourseLevel ?? "").trim();
        const cSemester = String(c.Semester ?? c.CourseSemester ?? "").trim();

        const levelOk = cLevel === String(levelToFetch);
        const semesterOk = cSemester === String(requestedSession);

        if (!levelOk || !semesterOk) return false;

        if (carryoverOnly) {
          return isCarryoverCourse(c);
        }

        return true;
      });

      const regResp = await axios.get(`${API}/api/courses/registered`, {
        params: {
          matricNo,
          level: carryoverOnly ? levelToFetch : requestedLevel,
          session: requestedSession,
          academicSession,
        },
        headers,
      });

      const registeredList = Array.isArray(regResp.data) ? regResp.data : [];

      setAvailable(courseList);
      setRegistered(registeredList);
    } catch (err) {
      console.error("Error fetching course lists:", err);
      setAvailable([]);
      setRegistered([]);
    }
  };

  useEffect(() => {
    if (!canRegister) {
      setAvailable([]);
      setRegistered([]);
      return;
    }

    const useLevel = studentLevel || level;
    const useSession = studentSession || semester;

    fetchCourseLists(
      useLevel,
      useSession,
      studentDepartment,
      showCarryoverOnly,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    programmeName,
    studentLevel,
    studentSession,
    studentDepartment,
    canRegister,
    showCarryoverOnly,
    academicSession,
  ]);

  useEffect(() => {
    if (!available || !available.length) {
      setSelected(new Set());
      return;
    }
    setSelected((prev) => {
      const keep = new Set();
      const availableIds = new Set(available.map((c) => c.CourseID));
      prev.forEach((id) => {
        if (availableIds.has(id)) keep.add(id);
      });
      return keep;
    });
  }, [available]);

  const toggleSelect = (id) => {
    if (!canRegister) {
      setMessage(
        paymentStatus
          ? "You must complete payment before you can register courses."
          : "Registration is disabled until your payment status is verified.",
      );
      return;
    }

    const course = available.find((c) => c.CourseID === id);
    if (!course) return;

    if (studentDepartment) {
      const courseDept = (course.Department ?? course.department ?? "")
        .toString()
        .trim();
      if (courseDept && courseDept !== String(studentDepartment)) {
        setMessage("You can only select courses from your department.");
        return;
      }
    }

    if (studentSession && (course.Semester || course.CourseSemester)) {
      const courseSem = String(
        course.Semester ?? course.CourseSemester ?? "",
      ).trim();
      if (courseSem && courseSem !== String(studentSession)) {
        setMessage("This course is not for your current session/semester.");
        return;
      }
    }

    if (studentLevel && (course.Level || course.CourseLevel)) {
      const courseLevel = String(
        course.Level ?? course.CourseLevel ?? "",
      ).trim();
      const prevLevel = previousLevelOf(studentLevel);
      const allowed =
        courseLevel === String(studentLevel) ||
        (prevLevel &&
          courseLevel === String(prevLevel) &&
          isCarryoverCourse(course));
      if (!allowed) {
        setMessage(
          "You may only select courses for your level, or carryover courses from the previous level.",
        );
        return;
      }
    }

    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      setMessage("");
      return s;
    });
  };

  const handleRegister = async () => {
    if (!canRegister) {
      setMessage("Registration blocked — payment not confirmed.");
      return;
    }
    if (!selected.size) {
      setMessage("No courses selected.");
      return;
    }

    const availableIds = new Set(available.map((c) => c.CourseID));
    for (const id of Array.from(selected)) {
      if (!availableIds.has(id)) {
        setMessage(
          "One or more selected courses are not available for your session/department.",
        );
        return;
      }
    }

    const selectedIds = Array.from(selected);
    const selectedCourses = available.filter((c) =>
      selectedIds.includes(c.CourseID),
    );

    const backupAvailable = available.slice();
    const backupRegistered = registered.slice();
    setProcessingRegister(true);
    setMessage("Registering selected courses...");
    setRegistered((r) => [...r, ...selectedCourses]);
    setAvailable((a) => a.filter((c) => !selectedIds.includes(c.CourseID)));
    setSelected(new Set());

    try {
      const resp = await axios.post(
        `${API}/api/courses/register`,
        {
          matricNo,
          selectedCourseIds: selectedIds,
          session: studentSession || semester,
          academicSession,
        },
        { headers: authHeaders() },
      );

      if (resp?.data?.msg) setMessage(resp.data.msg);
      else setMessage("Courses registered successfully.");

      const useLevel = studentLevel || level;
      const useSession = studentSession || semester;
      await fetchCourseLists(
        useLevel,
        useSession,
        studentDepartment,
        showCarryoverOnly,
      );
    } catch (err) {
      console.error("Register error:", err);
      setAvailable(backupAvailable);
      setRegistered(backupRegistered);
      if (err?.response?.data?.msg) setMessage(err.response.data.msg);
      else setMessage("Failed to register courses. Please try again.");
    } finally {
      setProcessingRegister(false);
    }
  };

  const handleRemove = async (courseId, sessionValue, academicValue) => {
    const backupRegistered = registered.slice();
    const backupAvailable = available.slice();

    setProcessingRemoveIds((s) => new Set([...s, courseId]));
    setRegistered((r) => r.filter((c) => c.CourseID !== courseId));

    const removedCourse = backupRegistered.find((c) => c.CourseID === courseId);
    if (removedCourse) {
      setAvailable((a) => [removedCourse, ...a]);
    }

    try {
      await axios.delete(`${API}/api/courses/register`, {
        params: {
          matricNo,
          courseId,
          session: sessionValue || studentSession || semester,
          academicSession: academicValue || academicSession,
        },
        headers: authHeaders(),
      });
      setMessage("Course removed.");
    } catch (err) {
      console.error("Remove error:", err);
      setRegistered(backupRegistered);
      setAvailable(backupAvailable);
      setMessage("Failed to remove course. Please try again.");
    } finally {
      setProcessingRemoveIds((s) => {
        const next = new Set(s);
        next.delete(courseId);
        return next;
      });
    }
  };

  const paymentPrompt = (
    <div className="payment-warning">
      <h3>Registration on hold</h3>
      <p>
        You are not eligible to register for courses because your school-fees
        payment for this session has not been confirmed. Please proceed to the
        <strong> Payments</strong> page to complete or verify payment.
      </p>
      <div style={{ marginTop: 12 }}>
        <button
          className="btn"
          onClick={() => (window.location.href = "/pay-fees")}
        >
          Go to Payments
        </button>
      </div>
    </div>
  );

  if (initialLoading) return <p>Loading…</p>;

  const registeredEmpty = !registered || registered.length === 0;
  const availableEmpty = !available || available.length === 0;

  return (
    <div className="courses-page" style={{ display: "flex", marginTop: "-130px" }}>
      <Sidebar />
      <main className="courses-main">
        <h1>Course Registration</h1>

        {!canRegister ? (
          paymentPrompt
        ) : (
          <>
            <div className="payment-ok">
              Payment confirmed — you may register courses.
            </div>

            <div className="selectors">
              <div className="student-info">
                {studentLevel && (
                  <span>
                    Your level: <strong>{studentLevel}</strong>
                  </span>
                )}
                {studentSession && (
                  <span>
                    {" "}
                    Your session: <strong>{studentSession}</strong>
                  </span>
                )}
                <span style={{ display: "block", marginTop: 6 }}>
                  Academic session: <strong>{academicSession}</strong>
                </span>
                {studentDepartment && (
                  <span>
                    {" "}
                    Your department: <strong>{studentDepartment}</strong>
                  </span>
                )}
              </div>

              <div style={{ marginTop: 12 }}>
                <button
                  className="btn"
                  onClick={() => setShowCarryoverOnly(false)}
                  style={{ marginRight: 8 }}
                >
                  Current Level Courses
                </button>
                <button
                  className="btn"
                  onClick={() => setShowCarryoverOnly(true)}
                >
                  Carryover Courses
                </button>
              </div>
            </div>

            <section>
              <h2>
                {showCarryoverOnly ? "Carryover Courses" : "Available Courses"}
              </h2>
              <div className="table-wrapper">
                <table className="courses-table">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Code</th>
                      <th>Title</th>
                      <th>Units</th>
                      <th>Dept</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableEmpty ? (
                      <tr>
                        <td colSpan="5">No courses available for this mode.</td>
                      </tr>
                    ) : (
                      available.map((c) => {
                        const carry = isCarryoverCourse(c);
                        const courseLevel = (
                          c.Level ??
                          c.CourseLevel ??
                          ""
                        ).toString();
                        const prevLevel = previousLevelOf(studentLevel);
                        const prevLevelCarry =
                          prevLevel &&
                          courseLevel === String(prevLevel) &&
                          carry;

                        return (
                          <tr
                            key={c.CourseID}
                            className={
                              selected.has(c.CourseID) ? "selected" : ""
                            }
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={selected.has(c.CourseID)}
                                onChange={() => toggleSelect(c.CourseID)}
                                disabled={!canRegister}
                                title={
                                  carry
                                    ? "Carryover course (previous level allowed)"
                                    : ""
                                }
                              />
                            </td>
                            <td>{c.CourseCode}</td>
                            <td>
                              {c.CourseTitle}
                              {carry && (
                                <span className="badge carryover">
                                  Carryover
                                </span>
                              )}
                              {prevLevelCarry && (
                                <span className="badge prev-level">
                                  Prev Level
                                </span>
                              )}
                            </td>
                            <td>{c.CreditUnits}</td>
                            <td>{c.Department}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <div className="actions">
                <button
                  className="register-btn"
                  onClick={handleRegister}
                  disabled={processingRegister || selected.size === 0}
                >
                  {processingRegister
                    ? "Registering…"
                    : `Register Selected (${selected.size})`}
                </button>
                {message && <p className="message">{message}</p>}
              </div>
            </section>

            <section>
              <h2>Registered Courses</h2>
              <div className="table-wrapper">

              <table className="courses-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Title</th>
                    <th>Units</th>
                    <th>Dept</th>
                    <th>Session</th>
                    <th>Academic Session</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {registeredEmpty ? (
                    <tr>
                      <td colSpan="7">No courses registered.</td>
                    </tr>
                  ) : (
                    registered.map((c) => (
                      <tr
                        key={`${c.CourseID}-${c.Session || ""}-${c.AcademicSession || ""}`}
                      >
                        <td>{c.CourseCode}</td>
                        <td>
                          {c.CourseTitle}
                          {isCarryoverCourse(c) && (
                            <span className="badge carryover">Carryover</span>
                          )}
                        </td>
                        <td>{c.CreditUnits}</td>
                        <td>{c.Department}</td>
                        <td>{c.Session || "-"}</td>
                        <td>{c.AcademicSession || "-"}</td>
                        <td>
                          <button
                            className="remove-btn"
                            onClick={() =>
                              handleRemove(
                                c.CourseID,
                                c.Session,
                                c.AcademicSession,
                              )
                            }
                            disabled={processingRemoveIds.has(c.CourseID)}
                          >
                            {processingRemoveIds.has(c.CourseID)
                              ? "Removing…"
                              : "Remove"}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
